"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"

import { cn } from "@workspace/ui/lib/utils"

/**
 * Desktop category quick-nav scroller for StoreHeader. The category row can
 * outgrow the header on tenants with many categories; instead of a raw
 * scrollbar this renders the list in a `no-scrollbar` overflow container and
 * overlays a gradient fade + chevron button on each *physical* edge that has
 * hidden content off-screen. Edges appear only when there is actually more to
 * scroll in that direction, so a short list shows no controls at all.
 *
 * Client component: edge visibility is measured from live layout (scroll
 * position + element widths) on scroll and on resize. The list markup itself
 * (`All menu` + the category links) is passed in as `children` from the
 * server-rendered header, so this component owns only the scroll mechanics.
 *
 * RTL-safe. All edge geometry is computed in *physical* terms (how much content
 * is hidden past the left/right edge) which the browser's standardized
 * `scrollLeft` model expresses identically for LTR and RTL: `scrollLeft` is 0 at
 * the reading-start position and moves negative as content scrolls toward the
 * physical left. The `prev`/`next` (reading-order) labels are mapped onto the
 * correct physical edge based on `dir`.
 */
export function CategoryScroller({
  dir,
  prevLabel,
  nextLabel,
  children,
}: {
  dir: "ltr" | "rtl"
  /** aria-label for the control that reveals earlier (reading-start) categories. */
  prevLabel: string
  /** aria-label for the control that reveals later (reading-end) categories. */
  nextLabel: string
  children: React.ReactNode
}) {
  const listRef = useRef<HTMLUListElement>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)

  const measure = useCallback(() => {
    const el = listRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    const sl = el.scrollLeft
    // Physical hidden extent on each edge. In RTL `scrollLeft` runs 0 → -max.
    const leftHidden = dir === "rtl" ? max + sl : sl
    const rightHidden = dir === "rtl" ? -sl : max - sl
    // 1px slack absorbs sub-pixel rounding so a fully-scrolled edge hides.
    setShowLeft(leftHidden > 1)
    setShowRight(rightHidden > 1)
  }, [dir])

  useEffect(() => {
    const el = listRef.current
    if (!el) return
    measure()
    el.addEventListener("scroll", measure, { passive: true })
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => {
      el.removeEventListener("scroll", measure)
      observer.disconnect()
    }
  }, [measure])

  // Physical-left button always scrolls toward the left (negative delta),
  // physical-right toward the right — identical math for LTR and RTL.
  const scrollByEdge = (direction: -1 | 1) => {
    const el = listRef.current
    if (!el) return
    el.scrollBy({ left: direction * el.clientWidth * 0.75, behavior: "smooth" })
  }

  // `prev` = reading-start; on LTR that edge is on the left, on RTL the right.
  const leftLabel = dir === "rtl" ? nextLabel : prevLabel
  const rightLabel = dir === "rtl" ? prevLabel : nextLabel

  // Edge masking (physical-left→right, identical for LTR/RTL). Instead of
  // painting an opaque gradient over the translucent+blurred header — which
  // stacks additively and shows as a brighter seam — we alpha-mask the list
  // itself so off-screen categories fade to transparent, revealing the real
  // blurred header behind. No extra background is painted, so nothing seams.
  const fade = "2rem"
  const leftStop = showLeft
    ? `transparent, black ${fade}`
    : `black, black ${fade}`
  const rightStop = showRight
    ? `black calc(100% - ${fade}), transparent`
    : `black calc(100% - ${fade}), black`
  const maskImage = `linear-gradient(to right, ${leftStop}, ${rightStop})`

  return (
    <div className="relative hidden min-w-0 flex-1 md:block">
      <ul
        ref={listRef}
        style={{ maskImage, WebkitMaskImage: maskImage }}
        className="no-scrollbar flex items-center gap-1 overflow-x-auto scroll-smooth text-sm"
      >
        {children}
      </ul>

      {/* Left edge: gradient fade + chevron, shown only when content is hidden
          past the physical-left edge. `pointer-events-none` on the fade keeps
          the underlying links hoverable; the button re-enables pointer events. */}
      <div
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 flex items-center ps-1 pe-6 transition-opacity duration-200",
          showLeft ? "opacity-100" : "opacity-0"
        )}
      >
        <button
          type="button"
          onClick={() => scrollByEdge(-1)}
          tabIndex={showLeft ? 0 : -1}
          aria-hidden={!showLeft}
          aria-label={leftLabel}
          className="pointer-events-auto inline-flex size-7 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm transition-colors hover:text-foreground"
        >
          <ChevronLeftIcon className="size-4" aria-hidden />
        </button>
      </div>

      {/* Right edge: mirror of the left. */}
      <div
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 flex items-center justify-end ps-6 pe-1 transition-opacity duration-200",
          showRight ? "opacity-100" : "opacity-0"
        )}
      >
        <button
          type="button"
          onClick={() => scrollByEdge(1)}
          tabIndex={showRight ? 0 : -1}
          aria-hidden={!showRight}
          aria-label={rightLabel}
          className="pointer-events-auto inline-flex size-7 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm transition-colors hover:text-foreground"
        >
          <ChevronRightIcon className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  )
}
